<template>
  <Container class="tw-mt-8">
    <div class="tw-mx-auto tw-max-w-5xl">
      <BaseButton
        class="tw-mb-6"
        color="outline-gray"
        href="/cities"
        is-inertia-link
      >
        <ArrowLeftIcon class="tw-mr-2 tw-h-4 tw-w-4" />
        Back to cities
      </BaseButton>

      <Card>
        <Alert
          v-if="!isUserGroupMember"
          type="warning"
          is-block
          class="tw-mb-6"
        >
          <template #title>
            You need to be a member to see other squad members and get a slack
            channel invite
          </template>
        </Alert>
        <div class="tw-flex tw-w-full tw-flex-wrap tw-justify-between">
          <h1 class="tw-display-2 tw-grow">
            {{ group.name }}
          </h1>
          <span class="tw-text-xs tw-text-neutral-400">
            created {{ formatRelativeDate(group.createdAt) }}
          </span>
        </div>
        <div class="tw-text-medium tw-mt-2 tw-text-xs tw-text-neutral-500">
          {{ group.currentMembersCount }} {{ membersCountLabel }} • Goal
          {{ group.maxMembers }} members
        </div>
        <div v-if="group.groupTagList.length" class="tw-mt-4">
          <Tag v-for="tag in group.groupTagList" :key="tag">
            {{ tag }}
          </Tag>
        </div>
        <hr class="tw-my-4" />
        <p>{{ group.description }}</p>
        <hr class="tw-my-4" />
        <div>
          <p class="tw-mb-2">
            {{
              isUserGroupMember
                ? 'Here is your slack channel link invite. We’ve also sent it to your mail box.'
                : 'Join this city squad to get a slack channel invite.'
            }}
          </p>
          <SlackChannel
            :is-user-group-member="isUserGroupMember"
            :channel="group.slackChannel"
            :channel-name="group.slackChannelName"
          />
        </div>
        <hr class="tw-my-4" />
        <GroupFooterButtons :group="group" />
        <template v-if="members.length">
          <hr class="tw-my-4" />
          <div class="tw-w-full">
            <div class="tw-caption-regular">Squad members</div>
            <div class="tw-mt-1 tw-divide-y tw-divide-neutral-100">
              <GroupMember
                class="tw-py-2 first:tw-pt-0 last:tw-pb-0"
                v-for="member in members"
                :key="member.profileUrl"
                :member="member"
                :is-blurred="!isUserGroupMember"
                test_id="group-member"
              />
            </div>
          </div>
        </template>
      </Card>
    </div>

    <UnlockFullAccessModal v-if="hasRole(ROLE.GUEST)" />
  </Container>
</template>

<script setup>
import GroupMember from '@/modules/growthmentor/groups/components/GroupMember.vue'
import GroupFooterButtons from '@/modules/growthmentor/groups/components/GroupFooterButtons.vue'
import SlackChannel from '@/modules/growthmentor/groups/components/SlackChannel.vue'
import Alert from '@/shared/components/Alert.vue'
import Container from '@/shared/components/Container.vue'
import Card from '@/shared/components/Card.vue'
import Tag from '@/shared/components/Tag.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import { OBJECT_PERMISSIONS, ROLE } from '@/shared/data/permissionSystem.js'
import { computed } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { useDateManager } from '@/shared/composables/useDateManager.js'

const props = defineProps({
  members: {
    type: Array,
    required: true,
  },
  group: {
    type: Object,
    required: true,
  },
})

const { hasObjectPermissions, hasRole } = usePermissionSystem()
const { formatRelativeDate } = useDateManager()

const membersCountLabel = computed(() => {
  return props.group.currentMembersCount === 1 ? 'member' : 'members'
})
const isUserGroupMember = computed(() => {
  return hasObjectPermissions(props.group, OBJECT_PERMISSIONS.GROUPS.APPROVED)
})
</script>
